import { z } from 'zod';

/** Batas nominal QRIS dari Komerce. VA BCA dipakai di atas batas ini. */
export const QRIS_MIN_AMOUNT = 10_000;
export const QRIS_MAX_AMOUNT = 10_000_000;

/** QRIS hangus 5 menit (standar QRIS); VA minimal 1 jam. */
export const QRIS_EXPIRY_SECONDS = 300;
export const VA_EXPIRY_SECONDS = 24 * 60 * 60;

export const PAYMENT_CHANNELS = ['QRIS', 'VA_BCA'] as const;
export type PaymentChannel = (typeof PAYMENT_CHANNELS)[number];

/**
 * FULL           : bayar penuh sekali.
 * DOWN_PAYMENT   : DP, membuat pesanan PAID dan boleh diproses.
 * SETTLEMENT     : pelunasan sisa tagihan setelah DP, wajib sebelum dikirim.
 */
export const PAYMENT_KINDS = ['FULL', 'DOWN_PAYMENT', 'SETTLEMENT'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

/**
 * Pesanan dengan subtotal produk (setelah diskon) DI ATAS angka ini boleh
 * dibayar DP. Keputusan CI 2026-09-14: dasar hitung subtotal produk, bukan
 * total; ongkir ditagih penuh bersama DP.
 */
export const DOWN_PAYMENT_MIN_SUBTOTAL = 3_000_000;
export const DOWN_PAYMENT_RATIO = 0.5;

export const createPaymentSchema = z.object({
  channel: z.enum(PAYMENT_CHANNELS),
  kind: z.enum(PAYMENT_KINDS).default('FULL'),
});

export const paymentParamsSchema = z.object({
  orderNumber: z.string().min(4).max(40),
});

export type CreatePaymentInput = z.infer<typeof createPaymentSchema>;
export type PaymentParams = z.infer<typeof paymentParamsSchema>;

/** Metode yang boleh dipakai untuk nominal tertentu. */
export function channelsForAmount(amount: number): PaymentChannel[] {
  const out: PaymentChannel[] = [];
  if (amount >= QRIS_MIN_AMOUNT && amount <= QRIS_MAX_AMOUNT) out.push('QRIS');
  if (amount >= QRIS_MIN_AMOUNT) out.push('VA_BCA');
  return out;
}

interface DownPaymentBase {
  subtotal: number;
  discount: number;
  shippingCost: number;
}

export function downPaymentAllowed(order: Pick<DownPaymentBase, 'subtotal' | 'discount'>): boolean {
  return order.subtotal - order.discount > DOWN_PAYMENT_MIN_SUBTOTAL;
}

/** DP = 50% subtotal produk (dibulatkan ke atas ke rupiah) + ongkir penuh. */
export function downPaymentAmount(order: DownPaymentBase): number {
  return Math.ceil((order.subtotal - order.discount) * DOWN_PAYMENT_RATIO) + order.shippingCost;
}
